/**
 * Blishka Hyperlocal Location & Serviceability Engine
 *
 * Configured for initial 5 km rollout:
 * - Operating Hub: Nehru Nagar / Smriti Nagar (Bhilai, CG)
 * - Maximum Delivery Radius: 5.0 km
 * - High-precision Haversine Distance Calculation
 * - Google Maps Pin Integration
 */

export interface ServiceHub {
  id: string;
  name: string;
  lat: number;
  lng: number;
  radiusKm: number;
  description: string;
}

export interface UserLocationState {
  status: 'unknown' | 'detecting' | 'serviceable' | 'unserviceable';
  areaName: string;
  lat?: number;
  lng?: number;
  distanceKm?: number;
  googleMapsUrl?: string;
  nearestHubName?: string;
  source: 'gps' | 'manual' | 'default';
  updatedAt: string;
}

export interface ServiceAreaOption {
  id: string;
  label: string;
  hubId: string;
  approxDistanceKm: number;
  isServiceable: boolean;
}

// ── 1. ACTIVE HUB: Hari Raj Pure Veg Restaurant (5.0 km radius) ──
export const HARI_RAJ_HUB: ServiceHub = {
  id: 'hub-hariraj-bhilai',
  name: 'Hari Raj Pure Veg Restaurant Hub',
  lat: 21.1872258,
  lng: 81.3436867,
  radiusKm: 5.0, // 5 km maximum service radius
  description: 'Serving 5 km radius from Hari Raj Pure Veg Restaurant, Bhilai'
};

export const ACTIVE_SERVICE_HUBS: ServiceHub[] = [HARI_RAJ_HUB];
export const MAX_SERVICE_RADIUS_KM = 5.0;

// ── 2. PRE-DEFINED BHILAI LOCATIONS (Requested Available Areas + Single Others Option) ──
export interface BhilaiLocationItem {
  id: string;
  name: string;
  isServiceable: boolean;
  lat?: number;
  lng?: number;
  city?: string;
  sortOrder?: number;
}

export const DEFAULT_BHILAI_AREAS: BhilaiLocationItem[] = [
  { id: 'area-power-house', name: 'Power House', isServiceable: true, lat: 21.1882, lng: 81.3452, city: 'Bhilai', sortOrder: 1 },
  { id: 'area-sector-1', name: 'Sector 1', isServiceable: true, lat: 21.1960, lng: 81.3320, city: 'Bhilai', sortOrder: 2 },
  { id: 'area-sector-2', name: 'Sector 2', isServiceable: true, lat: 21.1930, lng: 81.3250, city: 'Bhilai', sortOrder: 3 },
  { id: 'area-sector-3', name: 'Sector 3', isServiceable: true, lat: 21.1910, lng: 81.3190, city: 'Bhilai', sortOrder: 4 },
  { id: 'area-sector-4', name: 'Sector 4', isServiceable: true, lat: 21.1890, lng: 81.3140, city: 'Bhilai', sortOrder: 5 },
  { id: 'area-sector-5', name: 'Sector 5', isServiceable: true, lat: 21.1860, lng: 81.3090, city: 'Bhilai', sortOrder: 6 },
  { id: 'area-sector-6', name: 'Sector 6', isServiceable: true, lat: 21.1850, lng: 81.3030, city: 'Bhilai', sortOrder: 7 },
  { id: 'area-sector-7', name: 'Sector 7', isServiceable: true, lat: 21.1830, lng: 81.2980, city: 'Bhilai', sortOrder: 8 },
  { id: 'area-sector-8', name: 'Sector 8', isServiceable: true, lat: 21.1800, lng: 81.2930, city: 'Bhilai', sortOrder: 9 },
  { id: 'area-sector-9', name: 'Sector 9', isServiceable: true, lat: 21.1780, lng: 81.2880, city: 'Bhilai', sortOrder: 10 },
  { id: 'area-sector-10', name: 'Sector 10', isServiceable: true, lat: 21.1750, lng: 81.2830, city: 'Bhilai', sortOrder: 11 },
  { id: 'area-kohka', name: 'Kohka', isServiceable: true, lat: 21.2150, lng: 81.3250, city: 'Bhilai', sortOrder: 12 },
  { id: 'area-pragati-nagar', name: 'Pragati Nagar', isServiceable: true, lat: 21.2050, lng: 81.3150, city: 'Bhilai', sortOrder: 13 },
  { id: 'area-risali', name: 'Risali', isServiceable: true, lat: 21.1600, lng: 81.3300, city: 'Bhilai', sortOrder: 14 },
  { id: 'area-vaishali-nagar', name: 'Vaishali Nagar', isServiceable: true, lat: 21.2100, lng: 81.3350, city: 'Bhilai', sortOrder: 15 },
  { id: 'area-supela', name: 'Supela', isServiceable: true, lat: 21.2020, lng: 81.3390, city: 'Bhilai', sortOrder: 16 },
  { id: 'area-others', name: 'Others (Coming Soon)', isServiceable: false, city: 'Bhilai', sortOrder: 99 }
];

export const STORAGE_OPERATIONAL_AREAS_KEY = 'blishka_operational_areas_v1';
export const OPERATIONAL_AREAS_CHANGED_EVENT = 'blishka_operational_areas_changed';

function syncMemoryArrays(items: BhilaiLocationItem[]) {
  BHILAI_AVAILABLE_AREAS.length = 0;
  BHILAI_AVAILABLE_AREAS.push(...items);

  ALLOWED_CART_AREAS.length = 0;
  ALLOWED_CART_AREAS.push(...items.filter((l) => l.isServiceable).map((l) => l.name));
}

function emitOperationalAreasChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPERATIONAL_AREAS_CHANGED_EVENT));
  }
}

/**
 * Returns active operational areas from localStorage or default seed
 */
export function getOperationalAreas(): BhilaiLocationItem[] {
  if (typeof window === 'undefined') {
    return [...DEFAULT_BHILAI_AREAS];
  }
  try {
    const raw = localStorage.getItem(STORAGE_OPERATIONAL_AREAS_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_OPERATIONAL_AREAS_KEY, JSON.stringify(DEFAULT_BHILAI_AREAS));
      syncMemoryArrays(DEFAULT_BHILAI_AREAS);
      return [...DEFAULT_BHILAI_AREAS];
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      syncMemoryArrays(parsed);
      return parsed;
    }
  } catch (err) {
    console.error('Failed to read operational areas from localStorage:', err);
  }
  syncMemoryArrays(DEFAULT_BHILAI_AREAS);
  return [...DEFAULT_BHILAI_AREAS];
}

/**
 * Returns array of names of currently serviceable areas
 */
export function getAllowedCartAreas(): string[] {
  const areas = getOperationalAreas();
  return areas.filter((l) => l.isServiceable).map((l) => l.name);
}

export const BHILAI_AVAILABLE_AREAS: BhilaiLocationItem[] = [...DEFAULT_BHILAI_AREAS];
export const BHILAI_LOCATIONS = BHILAI_AVAILABLE_AREAS;

export const PREDEFINED_AREAS: ServiceAreaOption[] = DEFAULT_BHILAI_AREAS.map((loc) => ({
  id: loc.id,
  label: loc.name,
  hubId: HARI_RAJ_HUB.id,
  approxDistanceKm: 0,
  isServiceable: loc.isServiceable
}));

export const ALLOWED_CART_AREAS: string[] = DEFAULT_BHILAI_AREAS
  .filter((l) => l.isServiceable)
  .map((l) => l.name);

// Initial synchronization on module load
if (typeof window !== 'undefined') {
  try {
    getOperationalAreas();
  } catch {}
}

/**
 * Saves or updates an operational area locally and propagates changes
 */
export function saveOperationalAreaLocal(area: BhilaiLocationItem): BhilaiLocationItem[] {
  const current = getOperationalAreas();
  const existingIdx = current.findIndex((a) => a.id === area.id || a.name.toLowerCase().trim() === area.name.toLowerCase().trim());

  let updated: BhilaiLocationItem[];
  if (existingIdx >= 0) {
    updated = [...current];
    updated[existingIdx] = { ...updated[existingIdx], ...area };
  } else {
    // Insert new area before the "Others" option if present
    const othersIdx = current.findIndex((a) => a.id === 'area-others' || a.name.toLowerCase().includes('other'));
    if (othersIdx >= 0) {
      updated = [
        ...current.slice(0, othersIdx),
        area,
        ...current.slice(othersIdx)
      ];
    } else {
      updated = [...current, area];
    }
  }

  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_OPERATIONAL_AREAS_KEY, JSON.stringify(updated));
  }
  syncMemoryArrays(updated);
  emitOperationalAreasChanged();
  return updated;
}

/**
 * Deletes/removes an operational area locally
 */
export function deleteOperationalAreaLocal(id: string): BhilaiLocationItem[] {
  const current = getOperationalAreas();
  const updated = current.filter((a) => a.id !== id);

  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_OPERATIONAL_AREAS_KEY, JSON.stringify(updated));
  }
  syncMemoryArrays(updated);
  emitOperationalAreasChanged();
  return updated;
}

/**
 * Toggles serviceable status of an operational area locally
 */
export function toggleAreaServiceableLocal(id: string, isServiceable: boolean): BhilaiLocationItem[] {
  const current = getOperationalAreas();
  const updated = current.map((a) => (a.id === id ? { ...a, isServiceable } : a));

  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_OPERATIONAL_AREAS_KEY, JSON.stringify(updated));
  }
  syncMemoryArrays(updated);
  emitOperationalAreasChanged();
  return updated;
}

/**
 * Resets operational areas back to factory defaults
 */
export function resetOperationalAreasLocal(): BhilaiLocationItem[] {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_OPERATIONAL_AREAS_KEY, JSON.stringify(DEFAULT_BHILAI_AREAS));
  }
  syncMemoryArrays(DEFAULT_BHILAI_AREAS);
  emitOperationalAreasChanged();
  return [...DEFAULT_BHILAI_AREAS];
}

/**
 * Syncs operational areas from backend / Cloudflare D1
 */
export async function syncOperationalAreasFromRemote(adminPin?: string): Promise<{
  success: boolean;
  areas: BhilaiLocationItem[];
  isD1Connected: boolean;
}> {
  try {
    const pin = adminPin || (typeof window !== 'undefined' ? sessionStorage.getItem('blishka_admin_pin') || '7994' : '7994');
    const res = await fetch('/api/operational-areas', {
      headers: {
        'Cache-Control': 'no-store',
        'x-admin-pin': pin
      }
    });

    if (!res.ok) {
      throw new Error(`Server returned HTTP ${res.status}`);
    }

    const data = (await res.json()) as any;
    if (data.success && Array.isArray(data.areas) && data.areas.length > 0) {
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_OPERATIONAL_AREAS_KEY, JSON.stringify(data.areas));
      }
      syncMemoryArrays(data.areas);
      emitOperationalAreasChanged();
      return { success: true, areas: data.areas, isD1Connected: Boolean(data.isD1Connected) };
    }
  } catch (err) {
    console.warn('Could not sync operational areas from remote; using local cache:', err);
  }

  const local = getOperationalAreas();
  return { success: false, areas: local, isD1Connected: false };
}

/**
 * Remote Super Admin: Add or update an operational area in Cloudflare D1 + localStorage
 */
export async function saveOperationalAreaRemote(
  area: BhilaiLocationItem,
  adminPin?: string
): Promise<{ success: boolean; area?: BhilaiLocationItem; error?: string }> {
  // Always update locally first for immediate responsiveness
  saveOperationalAreaLocal(area);

  try {
    const pin = adminPin || (typeof window !== 'undefined' ? sessionStorage.getItem('blishka_admin_pin') || '7994' : '7994');
    const res = await fetch('/api/operational-areas', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify(area)
    });

    const data = (await res.json()) as any;
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to save to cloud server' };
    }
    return { success: true, area: data.area || area };
  } catch (err: any) {
    // Local save succeeded, cloud offline/unreachable
    return { success: true, area, error: 'Saved locally (cloud offline)' };
  }
}

/**
 * Remote Super Admin: Remove an operational area from Cloudflare D1 + localStorage
 */
export async function deleteOperationalAreaRemote(
  id: string,
  adminPin?: string
): Promise<{ success: boolean; error?: string }> {
  // Update locally first
  deleteOperationalAreaLocal(id);

  try {
    const pin = adminPin || (typeof window !== 'undefined' ? sessionStorage.getItem('blishka_admin_pin') || '7994' : '7994');
    const res = await fetch(`/api/operational-areas?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': pin
      }
    });

    const data = (await res.json()) as any;
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to remove from cloud server' };
    }
    return { success: true };
  } catch (err: any) {
    return { success: true, error: 'Removed locally (cloud offline)' };
  }
}

/**
 * Remote Super Admin: Toggle serviceable status in Cloudflare D1 + localStorage
 */
export async function toggleAreaServiceableRemote(
  id: string,
  isServiceable: boolean,
  adminPin?: string
): Promise<{ success: boolean; error?: string }> {
  toggleAreaServiceableLocal(id, isServiceable);

  try {
    const pin = adminPin || (typeof window !== 'undefined' ? sessionStorage.getItem('blishka_admin_pin') || '7994' : '7994');
    const res = await fetch('/api/operational-areas', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify({ id, isServiceable })
    });

    const data = (await res.json()) as any;
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to update cloud server' };
    }
    return { success: true };
  } catch (err: any) {
    return { success: true, error: 'Updated locally (cloud offline)' };
  }
}

/**
 * Remote Super Admin: Reset operational areas in Cloudflare D1 + localStorage
 */
export async function resetOperationalAreasRemote(adminPin?: string): Promise<{ success: boolean; error?: string }> {
  resetOperationalAreasLocal();

  try {
    const pin = adminPin || (typeof window !== 'undefined' ? sessionStorage.getItem('blishka_admin_pin') || '7994' : '7994');
    const res = await fetch('/api/operational-areas', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify({ action: 'reset_to_defaults' })
    });

    const data = (await res.json()) as any;
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to reset on cloud server' };
    }
    return { success: true };
  } catch (err: any) {
    return { success: true, error: 'Reset locally (cloud offline)' };
  }
}


export const STORAGE_LOCATION_KEY = 'blishka_customer_location';
export const STORAGE_WAITLIST_KEY = 'blishka_expansion_waitlist';
export const STORAGE_GEO_PROMPTED_KEY = 'blishka_geo_prompted_once';
export const STORAGE_GEO_PERMISSION_KEY = 'blishka_geo_permission_granted';
export const STORAGE_SAVED_ADDRESSES_KEY = 'blishka_customer_saved_addresses';

export interface SavedCustomerAddress {
  id: string;
  clientName: string;
  phone: string;
  address: string;
  area: string;
  pincode?: string;
  lat?: number;
  lng?: number;
  googleMapsUrl?: string;
  isServiceable: boolean;
  lastUsedAt?: string;
}

/**
 * Strips prefix and noise from area names for ultra-clean UI badges
 */
export function cleanAreaName(areaName: string): string {
  if (!areaName) return 'your Area';
  return (
    areaName
      .replace(/^Bhilai\s*-\s*/i, '')
      .replace(/^Durg\s*-\s*/i, '')
      .replace(/,\s*Bhilai$/i, '')
      .replace(/,\s*Durg$/i, '')
      .split('(')[0]
      .trim() || 'your Area'
  );
}

/**
 * Checks whether an area or coordinates are in the active deployed zone (within 5 km of Hari Raj)
 */
export function isAreaServiceable(areaName: string, lat?: number, lng?: number): boolean {
  if (lat !== undefined && lng !== undefined) {
    const check = checkCoordinatesServiceable(lat, lng);
    return check.isServiceable;
  }
  const clean = (areaName || '').toLowerCase().trim();
  if (clean.includes('other')) return false;
  const allowed = getAllowedCartAreas();
  return allowed.some((allowedItem) => {
    const itemLower = allowedItem.toLowerCase().trim();
    return clean.includes(itemLower) || itemLower.includes(clean);
  });
}

/**
 * Finds the closest locality label for GPS coordinates
 */
export function findClosestLocality(
  lat: number,
  lng: number
): {
  name: string;
  isServiceable: boolean;
  distanceKm: number;
} {
  const check = checkCoordinatesServiceable(lat, lng);

  // If outside 5km from Hari Raj, it is unserviceable
  if (!check.isServiceable) {
    return {
      name: 'Others (Coming Soon)',
      isServiceable: false,
      distanceKm: check.distanceKm
    };
  }

  const areas = getOperationalAreas();
  // Find closest recognizable Bhilai location
  let closest = areas.find((a) => a.isServiceable && a.lat !== undefined && a.lng !== undefined) || areas[0] || DEFAULT_BHILAI_AREAS[0];
  let minDistance = Infinity;

  for (const item of areas) {
    if (item.lat !== undefined && item.lng !== undefined) {
      const d = calculateHaversineDistance(lat, lng, item.lat, item.lng);
      if (d < minDistance) {
        minDistance = d;
        closest = item;
      }
    }
  }

  return {
    name: closest ? closest.name : 'Power House',
    isServiceable: closest ? closest.isServiceable : true,
    distanceKm: check.distanceKm
  };
}

/**
 * Retrieves saved customer addresses from previous bookings or direct storage
 */
export function getSavedCustomerAddresses(): SavedCustomerAddress[] {
  const addresses: SavedCustomerAddress[] = [];
  const seenKeys = new Set<string>();

  // 1. Explicit saved addresses storage
  try {
    const raw = localStorage.getItem(STORAGE_SAVED_ADDRESSES_KEY);
    if (raw) {
      const list: SavedCustomerAddress[] = JSON.parse(raw);
      if (Array.isArray(list)) {
        for (const item of list) {
          if (!item.address && !item.area) continue;
          const key = `${(item.address || '').trim().toLowerCase()}|${(item.area || '').trim().toLowerCase()}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            addresses.push({
              ...item,
              isServiceable: isAreaServiceable(item.area, item.lat, item.lng)
            });
          }
        }
      }
    }
  } catch {
    // ignore
  }

  // 2. Scan past bookings log from localStorage ('blishka_bookings_log')
  try {
    const rawBookings = localStorage.getItem('blishka_bookings_log');
    if (rawBookings) {
      const bookings = JSON.parse(rawBookings);
      if (Array.isArray(bookings)) {
        for (const b of bookings) {
          if (!b.address && !b.area) continue;
          const key = `${(b.address || '').trim().toLowerCase()}|${(b.area || '').trim().toLowerCase()}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            const isServ = isAreaServiceable(b.area, b.latitude, b.longitude);
            addresses.push({
              id: `addr-${b.bookingRef || Math.random().toString(36).slice(2, 8)}`,
              clientName: b.clientName || 'Customer',
              phone: b.phone || '',
              address: b.address || '',
              area: b.area || 'Bhilai - Sector 6',
              pincode: b.pincode || '',
              lat: b.latitude,
              lng: b.longitude,
              googleMapsUrl: b.googleMapsUrl,
              isServiceable: isServ,
              lastUsedAt: b.timestamp || new Date().toISOString()
            });
          }
        }
      }
    }
  } catch {
    // ignore
  }

  return addresses;
}

/**
 * Saves or updates a customer address into local storage
 */
export function saveCustomerAddress(
  addr: Omit<SavedCustomerAddress, 'id' | 'lastUsedAt'>
): void {
  try {
    const existing = getSavedCustomerAddresses();
    const key = `${(addr.address || '').trim().toLowerCase()}|${(addr.area || '').trim().toLowerCase()}`;
    const filtered = existing.filter(
      (a) => `${(a.address || '').trim().toLowerCase()}|${(a.area || '').trim().toLowerCase()}` !== key
    );

    const newRecord: SavedCustomerAddress = {
      ...addr,
      id: `addr-${Date.now()}`,
      lastUsedAt: new Date().toISOString()
    };

    const updated = [newRecord, ...filtered].slice(0, 10);
    localStorage.setItem(STORAGE_SAVED_ADDRESSES_KEY, JSON.stringify(updated));

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('blishka-saved-addresses-changed'));
    }
  } catch {
    // ignore
  }
}

/**
 * Calculates straight-line distance in kilometers using the Haversine formula
 */
export function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
}

/**
 * Verifies if coordinates are within the 5 km radius of any active hub
 */
export function checkCoordinatesServiceable(
  lat: number,
  lng: number
): {
  isServiceable: boolean;
  distanceKm: number;
  hub: ServiceHub;
} {
  let minDistance = Infinity;
  let nearestHub = ACTIVE_SERVICE_HUBS[0];

  for (const hub of ACTIVE_SERVICE_HUBS) {
    const dist = calculateHaversineDistance(lat, lng, hub.lat, hub.lng);
    if (dist < minDistance) {
      minDistance = dist;
      nearestHub = hub;
    }
  }

  const isServiceable = minDistance <= nearestHub.radiusKm;
  return {
    isServiceable,
    distanceKm: minDistance,
    hub: nearestHub
  };
}

/**
 * Generates a clean Google Maps Pin URL from coordinates
 */
export function makeGoogleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/**
 * Loads cached user location from localStorage
 */
export function loadSavedUserLocation(): UserLocationState | null {
  try {
    const raw = localStorage.getItem(STORAGE_LOCATION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UserLocationState;
  } catch {
    return null;
  }
}

/**
 * Saves user location state to localStorage
 */
export function saveUserLocation(loc: UserLocationState): void {
  try {
    localStorage.setItem(STORAGE_LOCATION_KEY, JSON.stringify(loc));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('blishka-location-changed', { detail: loc })
      );
    }
  } catch {
    // ignore
  }
}

/**
 * Detects live GPS position using browser Geolocation API
 */
export function detectLiveGeolocation(options?: { forceFresh?: boolean }): Promise<{
  success: boolean;
  lat?: number;
  lng?: number;
  accuracy?: number;
  distanceKm?: number;
  isServiceable?: boolean;
  googleMapsUrl?: string;
  error?: string;
}> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({
        success: false,
        error: 'Geolocation is not supported by your browser.'
      });
      return;
    }

    // When forceFresh is requested (e.g. during checkout), clear cached state so browser/app treats it freshly
    if (options?.forceFresh) {
      try {
        localStorage.removeItem(STORAGE_GEO_PERMISSION_KEY);
      } catch {
        // ignore
      }
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const accuracy = Math.round(pos.coords.accuracy);

        const check = checkCoordinatesServiceable(lat, lng);
        const mapsUrl = makeGoogleMapsUrl(lat, lng);

        // Record permission granted
        try {
          localStorage.setItem(STORAGE_GEO_PERMISSION_KEY, 'true');
          localStorage.setItem(STORAGE_GEO_PROMPTED_KEY, 'true');
        } catch {
          // ignore
        }

        resolve({
          success: true,
          lat,
          lng,
          accuracy,
          distanceKm: check.distanceKm,
          isServiceable: check.isServiceable,
          googleMapsUrl: mapsUrl
        });
      },
      (err) => {
        let msg = 'Unable to detect location.';
        if (err.code === err.PERMISSION_DENIED) {
          msg = 'Location access blocked. Tap 🔒 near address bar to allow.';
          try {
            localStorage.setItem(STORAGE_GEO_PERMISSION_KEY, 'false');
            localStorage.setItem(STORAGE_GEO_PROMPTED_KEY, 'true');
          } catch {
            // ignore
          }
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          msg = 'GPS unavailable. You can paste map link below.';
        } else if (err.code === err.TIMEOUT) {
          msg = 'GPS request timed out. Please try again.';
        }
        resolve({
          success: false,
          error: msg
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: options?.forceFresh ? 0 : 30000
      }
    );
  });
}

/**
 * Automatically initializes customer location on app load:
 * - Asks for location permission only the first time.
 * - Every other time, detects location automatically if permission is true.
 * - Never pesters the customer if they previously denied.
 */
export async function initCustomerLocationOnAppLoad(
  onLocationDetected?: (loc: UserLocationState) => void
): Promise<void> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.geolocation) {
    return;
  }

  let permissionGranted: boolean | null = null;

  // 1. Check navigator.permissions API if available
  if ('permissions' in navigator && (navigator as any).permissions?.query) {
    try {
      const status = await (navigator as any).permissions.query({ name: 'geolocation' });
      if (status.state === 'granted') {
        permissionGranted = true;
        localStorage.setItem(STORAGE_GEO_PERMISSION_KEY, 'true');
      } else if (status.state === 'denied') {
        permissionGranted = false;
        localStorage.setItem(STORAGE_GEO_PERMISSION_KEY, 'false');
      }
    } catch {
      // ignore
    }
  }

  // 2. Fall back to cached permission flag
  if (permissionGranted === null) {
    try {
      const saved = localStorage.getItem(STORAGE_GEO_PERMISSION_KEY);
      if (saved === 'true') permissionGranted = true;
      else if (saved === 'false') permissionGranted = false;
    } catch {
      // ignore
    }
  }

  const hasBeenPrompted = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_GEO_PROMPTED_KEY) === 'true';

  if (permissionGranted === true) {
    // Permission is TRUE: Automatically detect location on load every time!
    const res = await detectLiveGeolocation();
    if (res.success && res.lat !== undefined && res.lng !== undefined) {
      const closest = findClosestLocality(res.lat, res.lng);
      const isServ = res.isServiceable !== undefined ? res.isServiceable : closest.isServiceable;
      const dist = res.distanceKm !== undefined ? res.distanceKm : closest.distanceKm;

      const newLoc: UserLocationState = {
        status: isServ ? 'serviceable' : 'unserviceable',
        areaName: closest.name,
        lat: res.lat,
        lng: res.lng,
        distanceKm: dist,
        googleMapsUrl: res.googleMapsUrl,
        nearestHubName: ACTIVE_SERVICE_HUBS[0].name,
        source: 'gps',
        updatedAt: new Date().toISOString()
      };

      saveUserLocation(newLoc);
      if (onLocationDetected) onLocationDetected(newLoc);
    }
  } else if (!hasBeenPrompted) {
    // FIRST TIME ONLY: Prompt for location permission
    try {
      localStorage.setItem(STORAGE_GEO_PROMPTED_KEY, 'true');
    } catch {
      // ignore
    }

    const res = await detectLiveGeolocation();
    if (res.success && res.lat !== undefined && res.lng !== undefined) {
      try {
        localStorage.setItem(STORAGE_GEO_PERMISSION_KEY, 'true');
      } catch {
        // ignore
      }
      const closest = findClosestLocality(res.lat, res.lng);
      const isServ = res.isServiceable !== undefined ? res.isServiceable : closest.isServiceable;
      const dist = res.distanceKm !== undefined ? res.distanceKm : closest.distanceKm;

      const newLoc: UserLocationState = {
        status: isServ ? 'serviceable' : 'unserviceable',
        areaName: closest.name,
        lat: res.lat,
        lng: res.lng,
        distanceKm: dist,
        googleMapsUrl: res.googleMapsUrl,
        nearestHubName: ACTIVE_SERVICE_HUBS[0].name,
        source: 'gps',
        updatedAt: new Date().toISOString()
      };

      saveUserLocation(newLoc);
      if (onLocationDetected) onLocationDetected(newLoc);
    } else {
      try {
        localStorage.setItem(STORAGE_GEO_PERMISSION_KEY, 'false');
      } catch {
        // ignore
      }
    }
  }
}

/**
 * Captures waitlist submission when user is outside active 5 km zone
 */
export async function submitExpansionWaitlistLead(payload: {
  phone: string;
  areaName: string;
  colonyName?: string;
  lat?: number;
  lng?: number;
}): Promise<boolean> {
  const record = {
    ...payload,
    timestamp: new Date().toISOString()
  };

  // 1. Save locally
  try {
    const raw = localStorage.getItem(STORAGE_WAITLIST_KEY);
    const list = raw ? JSON.parse(raw) : [];
    list.push(record);
    localStorage.setItem(STORAGE_WAITLIST_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }

  // 2. Dispatch to backend lead API
  try {
    await fetch('/api/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: `Waitlist (${payload.colonyName || payload.areaName})`,
        email: `${payload.phone}@blishka-lead.local`,
        interest: `Expansion request: ${payload.colonyName || payload.areaName}`,
        notes: `Customer phone: ${payload.phone}, Area: ${payload.areaName}, Coordinates: ${payload.lat || ''},${payload.lng || ''}`
      })
    });
    return true;
  } catch {
    return true; // Still true since saved locally
  }
}
